import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Skeleton } from "../Needed";
import { ConfirmDialog, EmailPreview, PushPreview, SmsPreview, StatusChip } from "../Kit";
import WaChatPreview from "./WaChatPreview";
import {
    archiveLibraryItem, deleteLibraryItem, duplicateLibraryItem, openEditor, previewEditor, refreshLibrary, resetEditor,
    revertLibraryItem, saveEditor, shareToLibrary, submitLibraryItem, testEditor,
} from "../../Helpers/notificationConsoleApi";
import { CHANNEL_NAME, ChannelFields, contentOf, formOf } from "./fields";
import "./template-editor.css";

// The one template editor (Notification Centre redesign, R3.4). The same screen edits:
//   a Library template           target { kind: "library", libraryId? , channel? }
//   one event message's wording  target { kind: "event", eventKey, audience, channel, language }
//   one campaign message         target { kind: "campaign", campaignId, channel, language }
// Three columns: the fields (left), the live preview (centre), the details (right: values with their
// samples, a real booking as the sample, problems, versions, Meta status, where it is used). An event or
// campaign message either uses a template from the Library or is written here; what is written here is
// the message's own template, and "Share to Library" lets other messages use it too.
const WA_STATUS = {
    APPROVED: ["ok", "Approved"], FLAGGED: ["wait", "Approved, flagged"], PENDING: ["wait", "Waiting for Meta"], IN_APPEAL: ["wait", "Appeal at Meta"],
    REJECTED: ["bad", "Rejected"], PAUSED: ["bad", "Paused by Meta"], DISABLED: ["bad", "Disabled by Meta"], DRAFT: ["neutral", "Draft: not at Meta"], NOT_SYNCED: ["wait", "Not synced from Meta"],
};
const waChip = (status) => { const [tone, text] = WA_STATUS[String(status || "DRAFT").toUpperCase()] || ["neutral", String(status).toLowerCase()]; return <StatusChip tone={tone}>{text}</StatusChip>; };
const whenText = (value) => (value ? new Date(value).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "");
const notBlocking = (p) => /at Meta|not in the WhatsApp templates|not been submitted/.test(p.message);

// Immutable update of "a.b.0.c" style paths (objects and arrays).
const setIn = (obj, keys, value) => {
    if (!keys.length) return value;
    const [head, ...rest] = keys;
    const index = Array.isArray(obj) ? Number(head) : head;
    const next = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
    next[index] = setIn(obj?.[index], rest, value);
    return next;
};
const getIn = (obj, path) => path.split(".").reduce((v, k) => (v == null ? v : v[Array.isArray(v) ? Number(k) : k]), obj);

const TemplateEditor = ({ target: startTarget, title, subtitle, onClose, onSaved, openTab, languages: languageOptions }) => {
    const [target, setTarget] = useState(startTarget);
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [source, setSource] = useState("own");
    const [libraryId, setLibraryId] = useState("");
    const [enabled, setEnabled] = useState(true);
    const [saved, setSaved] = useState("");
    const [shown, setShown] = useState(null);
    const [sampleBookingId, setSampleBookingId] = useState("");
    const [busy, setBusy] = useState("");
    const [testTo, setTestTo] = useState("");
    const [confirm, setConfirm] = useState(null); // { title, text, label, danger, run }
    const [shareName, setShareName] = useState(null);
    const [pushLook, setPushLook] = useState("android");
    const [emailWidth, setEmailWidth] = useState(600);
    const focus = useRef({ path: "", start: 0, end: 0 });
    const els = useRef({});
    const kind = target.kind;
    const channel = data?.channel || target.channel;

    // ---- load ----
    const apply = useCallback((d) => {
        setData(d);
        if (d.target) setTarget(d.target);
        const nextForm = formOf(d.channel, d.content, { language: d.target?.language || "en", category: d.constraints?.whatsappCategory, smsType: d.constraints?.smsType, linkedFirst: d.target?.kind !== "library" });
        const nextSource = d.source === "library" ? "library" : "own";
        const nextLib = d.picked?.libraryId || "";
        const nextEnabled = d.enabled !== false;
        setForm(nextForm);
        setSource(nextSource);
        setLibraryId(nextLib);
        setEnabled(nextEnabled);
        setSaved(JSON.stringify({ form: nextForm, source: nextSource, libraryId: nextLib, enabled: nextEnabled }));
    }, []);
    const load = useCallback(async (t) => {
        setData(null);
        const result = await openEditor(t);
        if (!result.ok) { toast.error(result.message); return; }
        apply(result.data);
    }, [apply]);
    // The host may pass a new object with the same target on every render: reload only when it changes.
    const startKey = JSON.stringify(startTarget);
    useEffect(() => { load(JSON.parse(startKey)); }, [load, startKey]);

    const dirty = !!form && JSON.stringify({ form, source, libraryId, enabled }) !== saved;
    const payload = useMemo(() => (form ? {
        target, enabled, source: kind === "library" ? "own" : source, libraryId: source === "library" ? libraryId : "",
        content: contentOf(channel, form), sampleBookingId,
    } : null), [target, enabled, source, libraryId, form, channel, sampleBookingId, kind]);

    // ---- live preview, half a second after the last change ----
    useEffect(() => {
        if (!payload) return undefined;
        const timer = setTimeout(async () => {
            const result = await previewEditor(payload);
            if (result.ok) setShown(result.data);
        }, 450);
        return () => clearTimeout(timer);
    }, [payload]);

    // ---- editing: paths and chips that insert at the caret ----
    const setPath = (path, value) => setForm((prev) => setIn(prev, path.split("."), value));
    const remember = (path) => (e) => { focus.current = { path, start: e.target.selectionStart ?? 0, end: e.target.selectionEnd ?? 0 }; };
    const bind = (path) => ({
        value: getIn(form, path) ?? "",
        onChange: (e) => setPath(path, e.target.value),
        onSelect: remember(path), onClick: remember(path), onKeyUp: remember(path), onFocus: remember(path),
        ref: (el) => { els.current[path] = el; },
    });
    // Text at the caret of a box (or at its end when it was not clicked): the HTML email's picture tag.
    const insertAtCaret = (path, piece) => {
        const text = String(getIn(form, path) ?? "");
        const here = focus.current.path === path ? focus.current : { start: text.length, end: text.length };
        setPath(path, `${text.slice(0, here.start)}${piece}${text.slice(here.end)}`);
        focus.current = { path, start: here.start + piece.length, end: here.start + piece.length };
    };
    const insert = (name) => {
        const token = `{${name}}`;
        const { path, start, end } = focus.current;
        if (!path) return toast.info("Click in a box first, then on the detail to add");
        const text = String(getIn(form, path) ?? "");
        if (/\.buttons\.\d+\.url$/.test(path)) return setPath(path, `${text.replace(/\{[A-Za-z0-9_]+\}$/, "")}${token}`);
        const next = `${text.slice(0, start)}${token}${text.slice(end)}`;
        const caret = start + token.length;
        setPath(path, next);
        focus.current = { path, start: caret, end: caret };
        return requestAnimationFrame(() => { const el = els.current[path]; if (el) { el.focus(); el.setSelectionRange?.(caret, caret); } });
    };

    // ---- actions ----
    const run = async (key, work, done) => {
        setBusy(key);
        const result = await work();
        setBusy("");
        if (!result.ok) { toast.error(result.message); return null; }
        if (done) toast.success(typeof done === "function" ? done(result) : done);
        return result;
    };
    const save = async () => {
        const result = await run("save", () => saveEditor(payload), (r) => (r.data.unchanged ? "Nothing changed" : "Saved. Messages use it within about 30 seconds."));
        if (!result) return;
        apply(result.data);
        onSaved?.(result.data);
    };
    const test = () => run("test", () => testEditor({ ...payload, ...(channel === "push" ? {} : { to: testTo }) }), (r) => r.message || "Test sent, marked [Test]");
    const reload = () => load(target);
    const template = data?.template || null;
    const submit = () => setConfirm({
        title: template?.whatsapp?.submitted ? "Send the changes to Meta?" : "Submit this template to Meta?",
        text: template?.whatsapp?.submitted ? "It goes back to review; until Meta approves it again, messages that use it are not sent." : "Approval usually takes from a few minutes to a day. The Meta name can't be changed afterwards.",
        label: "Send to Meta",
        run: async () => { if (await run("submit", () => submitLibraryItem(template.libraryId), "Sent to Meta")) reload(); },
    });
    const checkMeta = async () => { if (await run("refresh", () => refreshLibrary(), (r) => r.message || "Checked")) reload(); };
    const bringBack = async (version) => { if (await run("revert", () => revertLibraryItem(template.libraryId, version), `Version ${version} is back (saved as a new version)`)) reload(); };
    const resetIt = () => setConfirm({
        title: kind === "campaign" ? "Remove this message?" : "Go back to the built-in wording?",
        text: kind === "campaign" ? "The campaign won't send on this channel in this language until a message is written again." : "The wording written here is removed. A Library template stays in the Library.",
        label: kind === "campaign" ? "Remove" : "Back to built-in", danger: true,
        run: async () => { const r = await run("reset", () => resetEditor(target), "Done"); if (r) { apply(r.data); onSaved?.(r.data); } },
    });
    const share = async () => {
        const r = await run("share", () => shareToLibrary({ target, libraryId: template.libraryId, name: shareName }), "In the Library now: other messages can use it");
        if (r) { setShareName(null); apply(r.data); onSaved?.(r.data); }
    };
    const duplicate = async () => {
        const r = await run("dup", () => duplicateLibraryItem(template.libraryId), "Copy made. You are now editing the copy.");
        if (r) load({ kind: "library", libraryId: r.data.template.libraryId });
    };
    const archive = async () => { if (await run("archive", () => archiveLibraryItem(template.libraryId, !template.archived), template.archived ? "Back in the Library" : "Archived")) reload(); };
    const remove = () => setConfirm({
        title: `Delete "${template.name}"?`,
        text: channel === "whatsapp" && template.whatsapp?.submitted ? "It is also deleted at Meta, and Meta won't let the same name be used again for 30 days. This can't be undone." : "This can't be undone.",
        label: "Delete", danger: true,
        run: async () => { if (await run("delete", () => deleteLibraryItem(template.libraryId), "Deleted")) onClose?.(); },
    });

    if (!data || !form) return <Skeleton height={360} />;

    const editable = data.editable !== false;
    const writing = kind === "library" || source === "own";
    const problems = shown?.problems || [];
    const blocking = problems.filter((p) => !notBlocking(p));
    const limits = data.limits || {};
    const catalogue = data.catalogue || [];
    const groups = catalogue.reduce((acc, v) => { const g = v.group || "Details"; (acc[g] = acc[g] || []).push(v); return acc; }, {});
    const lockedMetaName = template?.whatsapp?.submitted?.metaName || "";
    const wa = form.whatsapp;
    const authoredWa = channel === "whatsapp" && wa?.source !== "meta";
    const ownSaved = kind !== "library" && data.source === "own" && template;
    const sourceChip = kind === "library"
        ? (template ? <StatusChip tone={data.shared ? "info" : "own"}>{data.shared ? "In the Library" : "Private to one message"}</StatusChip> : <StatusChip tone="neutral">New</StatusChip>)
        : !enabled ? <StatusChip tone="neutral">Switched off</StatusChip>
            : data.source === "own" ? <StatusChip tone="own">Written here</StatusChip>
                : data.source === "library" ? <StatusChip tone="info">Library: {data.picked?.name}</StatusChip>
                    : data.source === "legacy" ? <StatusChip tone="wait">Written before the Library (save to move it)</StatusChip>
                        : data.source === "missing" ? <StatusChip tone="bad">Its template is gone</StatusChip>
                            : <StatusChip tone="neutral">Built-in wording</StatusChip>;
    const languageList = languageOptions || data.languages || [];

    // ---- preview column ----
    const preview = () => {
        if (kind !== "library" && !enabled) return <div className="kit-empty"><div className="kit-empty-title">Switched off</div><div className="kit-sub">Nothing goes on this channel for this message.</div></div>;
        if (!shown) return <Skeleton height={200} />;
        if (channel === "push") return <PushPreview title={shown.push?.title} body={shown.push?.body} image={shown.push?.image || ""} platform={pushLook} />;
        if (channel === "email") return <EmailPreview subject={shown.email?.subject} preheader={form.email?.preheader} html={shown.email?.html} width={emailWidth} />;
        if (channel === "sms") return <SmsPreview text={shown.sms?.text} />;
        if (shown.whatsapp?.linked) {
            return (
                <div className="te-wa-linked">
                    <div className="fw-semibold mb-1">Meta template {shown.whatsapp.name || "(none chosen)"}</div>
                    {shown.whatsapp.headerType === "LOCATION" ? <div className="te-wa-value"><span>Place</span><span>{shown.whatsapp.location ? `${shown.whatsapp.location.name || ""} ${shown.whatsapp.location.address || ""}`.trim() || `${shown.whatsapp.location.latitude}, ${shown.whatsapp.location.longitude}` : <em>no place chosen</em>}</span></div> : null}
                    {shown.whatsapp.headerType && shown.whatsapp.headerType !== "LOCATION" ? (shown.whatsapp.headerUrl && shown.whatsapp.headerType === "IMAGE" ? <img className="te-wa-header-img" src={shown.whatsapp.headerUrl} alt="" /> : <div className="te-wa-value"><span>Header</span><span>{shown.whatsapp.headerUrl ? `${shown.whatsapp.headerType.toLowerCase()} file` : <em>no file chosen</em>}</span></div>) : null}
                    <div className="kit-sub mb-2">Meta keeps the wording; these values fill it in.</div>
                    {shown.whatsapp.header !== null && shown.whatsapp.header !== undefined ? <div className="te-wa-value"><span>Header</span><span>{shown.whatsapp.header || <em>empty</em>}</span></div> : null}
                    {(shown.whatsapp.values || []).map((v, i) => <div key={i} className="te-wa-value"><span>{`{{${i + 1}}}`}</span><span>{v || <em>empty: the message would be skipped</em>}</span></div>)}
                    {shown.whatsapp.button ? <div className="te-wa-value"><span>Button</span><span>…/{shown.whatsapp.button}</span></div> : null}
                </div>
            );
        }
        return <WaChatPreview shown={shown.whatsapp || {}} />;
    };

    return (
        <div className="te">
            <div className="te-head">
                <div className="te-head-titles">
                    {onClose ? <button type="button" className="btn btn-link btn-sm px-0" onClick={onClose}>&larr; Back</button> : null}
                    <h2 className="te-title">{title || (template ? template.name : `New ${CHANNEL_NAME[channel].toLowerCase()} template`)}</h2>
                    <div className="kit-sub">{subtitle || CHANNEL_NAME[channel]}{data.row ? ` · version ${data.row.version}` : template ? ` · version ${template.version}` : ""}</div>
                </div>
                <div className="te-head-side">
                    {sourceChip}
                    {channel === "whatsapp" && template ? waChip(template.whatsapp?.status) : null}
                    {kind === "event" && languageList.length > 1 ? (
                        <select className="form-select form-select-sm w-auto" value={target.language} aria-label="Language" onChange={(e) => load({ ...target, language: e.target.value })}>
                            {languageList.map((l) => <option key={l.code} value={l.code}>{l.name || l.code}{l.isDefault ? " (default)" : ""}{l.enabled === false ? " - off" : ""}</option>)}
                        </select>
                    ) : null}
                </div>
            </div>
            {!editable ? <div className="te-note">This campaign has started or ended, so its messages can't be changed.</div> : null}

            <div className="te-grid">
                {/* ---- fields ---- */}
                <section className="te-col te-fields" aria-label="Fields">
                    {kind !== "library" ? (
                        <>
                            <div className="form-check form-switch mb-3">
                                <input className="form-check-input" type="checkbox" id="te-enabled" checked={enabled} disabled={!editable} onChange={() => setEnabled(!enabled)} />
                                <label className="form-check-label small" htmlFor="te-enabled">{enabled ? "Send on this channel" : "Switched off: nothing goes on this channel"}</label>
                            </div>
                            {enabled ? (
                                <div className="btn-group btn-group-sm mb-3" role="group" aria-label="Where the wording comes from">
                                    <button type="button" className={`btn ${source === "own" ? "btn-primary" : "btn-outline-primary"}`} disabled={!editable} onClick={() => setSource("own")}>Write it here</button>
                                    <button type="button" className={`btn ${source === "library" ? "btn-primary" : "btn-outline-primary"}`} disabled={!editable} onClick={() => setSource("library")}>Use a Library template</button>
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div className="row g-2 mb-3">
                            <div className="col-sm-7">
                                <label className="form-label small mb-1" htmlFor="te-name">Name (for you; customers don't see it)</label>
                                <input id="te-name" className="form-control form-control-sm" maxLength={limits.name || 80} {...bind("name")} />
                            </div>
                            <div className="col-sm-5">
                                <label className="form-label small mb-1" htmlFor="te-lang">Language</label>
                                {channel === "whatsapp" ? (
                                    wa?.source === "meta" ? <input id="te-lang" className="form-control form-control-sm" value={wa.linked?.languageCode || ""} disabled readOnly /> : <input id="te-lang" className="form-control form-control-sm" placeholder="en, ta, en_US" disabled={!!lockedMetaName} {...bind("language")} />
                                ) : (
                                    <select id="te-lang" className="form-select form-select-sm" value={form.language} onChange={(e) => setPath("language", e.target.value)}>
                                        {(languageList.length ? languageList : [{ code: "en", name: "English" }]).map((l) => <option key={l.code} value={l.code}>{l.name || l.code}</option>)}
                                    </select>
                                )}
                            </div>
                            <div className="col-12">
                                <label className="form-label small mb-1" htmlFor="te-desc">Note (optional: when to use it)</label>
                                <input id="te-desc" className="form-control form-control-sm" maxLength={limits.description || 300} {...bind("description")} />
                            </div>
                        </div>
                    )}

                    {kind !== "library" && enabled && source === "library" ? (
                        <div className="te-pick" role="radiogroup" aria-label="Library templates">
                            {data.source === "library" && data.picked?.problems?.length ? <div className="te-problem">The template chosen before can't be used now ({data.picked.problems.join("; ")}), so the built-in wording is sent. Choose another one.</div> : null}
                            {(data.candidates || []).length === 0 ? (
                                <div className="te-note">No {CHANNEL_NAME[channel]} templates in this language in the Library yet.{openTab ? <> <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => openTab("library")}>Open the Library</button></> : null}</div>
                            ) : data.candidates.map((t) => (
                                <label key={t.libraryId} className={`te-pick-item ${libraryId === t.libraryId ? "is-active" : ""} ${t.fits ? "" : "is-unfit"}`}>
                                    <input type="radio" name="te-pick" checked={libraryId === t.libraryId} disabled={!t.fits || !editable} onChange={() => setLibraryId(t.libraryId)} />
                                    <span>
                                        <span className="fw-semibold">{t.name}</span> {channel === "whatsapp" ? waChip(t.status) : null}
                                        <span className="kit-sub d-block">{t.summary}</span>
                                        {!t.fits ? <span className="kit-sub d-block te-bad">{t.problems.join("; ")}</span> : t.approved === false ? <span className="kit-sub d-block">Can be sent once Meta approves it.</span> : null}
                                    </span>
                                </label>
                            ))}
                            <div className="kit-sub mt-2">Changes made to a Library template reach every message that uses it.</div>
                        </div>
                    ) : null}

                    {writing && (kind === "library" || enabled) ? (
                        <ChannelFields channel={channel} form={form} setPath={setPath} bind={bind} limits={limits} catalogue={catalogue} whatsappTemplates={data.whatsappTemplates || []} disabled={!editable} lockedMetaName={lockedMetaName} kind={kind} constraints={data.constraints} mediaDetails={data.mediaDetails || []} insertAtCaret={insertAtCaret} />
                    ) : null}

                    {problems.length > 0 ? <div className="te-problem mt-3" role="alert">{problems.map((p) => <div key={`${p.field}${p.message}`}>{p.message}</div>)}</div> : null}

                    <div className="te-actions">
                        <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={!!busy || !editable || (!dirty && kind === "library") || blocking.length > 0}>{busy === "save" ? "Saving..." : dirty || kind !== "library" ? "Save" : "Saved"}</button>
                        {kind !== "library" && data.row ? <button type="button" className="btn btn-outline-danger btn-sm" onClick={resetIt} disabled={!!busy || !editable}>{kind === "campaign" ? "Remove" : "Back to built-in"}</button> : null}
                        {authoredWa && template && !dirty ? (
                            <button type="button" className="btn btn-success btn-sm" onClick={submit} disabled={!!busy || !data.whatsappReady} title={!data.whatsappReady ? "WhatsApp is not set up on this server" : ""}>{busy === "submit" ? "Sending..." : template.whatsapp?.submitted ? "Send changes to Meta" : "Submit to Meta"}</button>
                        ) : null}
                        {channel === "whatsapp" && template ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={checkMeta} disabled={!!busy}>{busy === "refresh" ? "Checking..." : "Check with Meta"}</button> : null}
                        {kind === "library" && template ? (
                            <>
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={duplicate} disabled={!!busy}>Duplicate</button>
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={archive} disabled={!!busy}>{template.archived ? "Unarchive" : "Archive"}</button>
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={remove} disabled={!!busy}>Delete</button>
                            </>
                        ) : null}
                    </div>
                    {authoredWa && template && dirty ? <div className="kit-sub mt-1">Save first, then send it to Meta.</div> : null}
                    {channel === "whatsapp" && !data.whatsappReady ? <div className="te-note">WhatsApp is not set up on this server, so templates can be written and saved but not submitted or tested.</div> : null}
                </section>

                {/* ---- preview ---- */}
                <section className="te-col te-preview" aria-label="Preview">
                    <div className="te-preview-head">
                        <span className="te-label">Preview</span>
                        {channel === "push" ? (
                            <div className="btn-group btn-group-sm" role="group" aria-label="Device">
                                <button type="button" className={`btn ${pushLook === "android" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setPushLook("android")}>Phone</button>
                                <button type="button" className={`btn ${pushLook === "web" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setPushLook("web")}>Browser</button>
                            </div>
                        ) : null}
                        {channel === "email" ? (
                            <div className="btn-group btn-group-sm" role="group" aria-label="Width">
                                <button type="button" className={`btn ${emailWidth === 600 ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setEmailWidth(600)}>Desktop</button>
                                <button type="button" className={`btn ${emailWidth === 360 ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setEmailWidth(360)}>Phone</button>
                            </div>
                        ) : null}
                    </div>
                    {preview()}
                    {(shown?.mediaDetails || []).map((m) => (
                        <div key={m.detail} className="kit-sub">{m.filled ? `${m.label}: from booking ${sampleBookingId}.` : `${m.label} is filled when each message is sent; this preview shows the fixed file instead. Choose a real booking under Sample to see it.`}</div>
                    ))}
                    <div className="te-test">
                        <div className="fw-semibold small mb-2">Send a test</div>
                        {channel !== "push" ? <input className="form-control form-control-sm mb-2" placeholder={channel === "email" ? "Email address" : "Phone number"} value={testTo} onChange={(e) => setTestTo(e.target.value)} aria-label="Send the test to" /> : null}
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={test} disabled={!!busy || blocking.length > 0 || (kind !== "library" && !enabled)}>{busy === "test" ? "Sending..." : channel === "push" ? "Send to my devices" : "Send test"}</button>
                        <div className="kit-sub mt-1">{channel === "whatsapp" ? "WhatsApp sends what Meta approved, with the sample." : "Uses what is on screen, saved or not, with the sample. Marked [Test]."}</div>
                    </div>
                </section>

                {/* ---- details ---- */}
                <aside className="te-col te-details" aria-label="Details">
                    {kind !== "campaign" ? (
                        <div className="te-block">
                            <label className="te-label" htmlFor="te-sample">Sample</label>
                            <select id="te-sample" className="form-select form-select-sm" value={sampleBookingId} onChange={(e) => setSampleBookingId(e.target.value)}>
                                <option value="">A made-up booking</option>
                                {(data.sampleBookings || []).map((b) => <option key={b} value={b}>Booking {b}</option>)}
                            </select>
                        </div>
                    ) : (
                        <div className="te-block"><span className="te-label">Sample</span><div className="kit-sub">A made-up person with this campaign's coupon and link.</div></div>
                    )}

                    {writing && (kind === "library" || enabled) ? (
                        <div className="te-block">
                            <span className="te-label">Details</span>
                            <div className="kit-sub mb-1">{channel === "sms" || (channel === "whatsapp" && wa?.source === "meta") ? "Choose them for each value on the left." : "Click in a box, then on a detail to add it."}</div>
                            {Object.entries(groups).map(([group, vars]) => (
                                <div key={group} className="te-chip-group">
                                    {Object.keys(groups).length > 1 ? <span className="te-chip-label">{group}</span> : null}
                                    {vars.map((v) => <button key={v.name} type="button" className="te-chip" title={`${v.label || v.name}${v.sample ? `, e.g. ${v.sample}` : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={() => insert(v.name)} disabled={channel === "sms" || (channel === "whatsapp" && wa?.source === "meta")}>{`{${v.name}}`}</button>)}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {shown?.variables?.length ? (
                        <div className="te-block">
                            <span className="te-label">Used here</span>
                            {shown.variables.map((n) => <div key={n} className="te-sample"><span className="te-mono">{`{${n}}`}</span><span className="kit-sub">{shown.samples?.[n] || "(empty in this sample)"}</span></div>)}
                        </div>
                    ) : null}

                    {ownSaved && editable ? (
                        <div className="te-block">
                            <span className="te-label">Share</span>
                            {shareName === null ? (
                                <>
                                    <div className="kit-sub mb-2">Written for this message only. Put it in the Library so other messages can use it too.</div>
                                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setShareName("")} disabled={dirty}>Share to Library</button>
                                    {dirty ? <div className="kit-sub mt-1">Save first.</div> : null}
                                </>
                            ) : (
                                <>
                                    <label className="form-label small mb-1" htmlFor="te-share">Name in the Library</label>
                                    <input id="te-share" className="form-control form-control-sm mb-2" maxLength={limits.name || 80} value={shareName} onChange={(e) => setShareName(e.target.value)} />
                                    <div className="d-flex gap-2">
                                        <button type="button" className="btn btn-primary btn-sm" onClick={share} disabled={!!busy || !shareName.trim()}>Share</button>
                                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShareName(null)}>Cancel</button>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : null}

                    {data.source === "library" && data.picked && openTab ? (
                        <div className="te-block"><button type="button" className="btn btn-link btn-sm px-0" onClick={() => openTab("library", { libraryId: data.picked.libraryId })}>Edit "{data.picked.name}" in the Library</button></div>
                    ) : null}

                    {kind === "library" && template ? (
                        <div className="te-block">
                            <span className="te-label">Used by {data.usedBy?.length || 0} message{data.usedBy?.length === 1 ? "" : "s"}</span>
                            {(data.usedBy || []).slice(0, 12).map((u) => <div key={u.templateId || `${u.eventKey}${u.audience}${u.language}`} className="kit-sub">{u.scope === "campaign" ? `Campaign ${u.campaignId}` : u.eventKey} · {u.audience} · {u.language}{u.enabled === false ? " · off" : ""}</div>)}
                        </div>
                    ) : null}

                    {template?.whatsapp?.reason ? <div className="te-block"><span className="te-label">Meta says</span><div className="kit-sub">{template.whatsapp.reason}</div></div> : null}
                    {template?.whatsapp?.changedSinceSubmit ? <div className="te-block te-note">Saved changes are not at Meta yet; customers get the approved wording.</div> : null}

                    {(data.history || []).length ? (
                        <div className="te-block">
                            <span className="te-label">Earlier versions</span>
                            {data.history.map((h) => (
                                <div key={h.version} className="te-version">
                                    <span>v{h.version} <span className="kit-sub">{whenText(h.at)}{h.by ? ` · ${h.by}` : ""}</span></span>
                                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => bringBack(h.version)} disabled={!!busy || !editable}>Bring back</button>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {kind === "event" && data.builtIn?.push ? (
                        <div className="te-block">
                            <span className="te-label">Built-in wording</span>
                            <div className="kit-sub"><strong>{data.builtIn.push.title}</strong><br />{data.builtIn.push.body}</div>
                        </div>
                    ) : null}
                </aside>
            </div>

            <ConfirmDialog
                open={!!confirm} title={confirm?.title} text={confirm?.text} confirmLabel={confirm?.label} danger={confirm?.danger} busy={!!busy}
                onCancel={() => setConfirm(null)}
                onConfirm={async () => { const next = confirm; setConfirm(null); await next.run(); }}
            />
        </div>
    );
};

export default TemplateEditor;
